import React from "react";
import { useNavigate } from "react-router-dom";
import { Clock, Sparkles, Check, Eye } from "lucide-react";
import PredictButton from "../../../../components/dashboards/components/shared/PredictButton";
import ViewOddsButton from "../../../../components/dashboards/components/shared/ViewOddsButton";
import XPicksButton from "../../../../components/dashboards/components/shared/XPicksButton";
import PredictionBox from "../../../../components/dashboards/components/shared/PredictionBox";
import { getBrazilianStatus } from "../../../../utils/detectBrazil";
import { footballDashboardPtBR } from "../../../../components/mappings/sports-dash-mappings/football-dashboard.pt-br";
import { rtdbFootballService as FootballService } from "../rtdb";
import { useAddToChat } from "../../../../components/chat/chatContext";

const statusStyles = (status) => {
  if (['1H', 'HT', '2H', 'ET', 'BT', 'P', 'INT', 'LIVE'].includes(status)) {
    return { label: status === 'HT' ? 'HT' : 'LIVE', badge: 'bg-red-500 text-white' };
  }
  if (['FT', 'AET', 'PEN'].includes(status)) {
    return { label: 'FT', badge: 'bg-white/20 text-white' };
  }
  return { label: 'Not Started', badge: 'bg-white/10 text-gray-300' };
};

const FootballDesktopGameCard = ({
  fixture,
  isSelected,
  isFrozen = false,
  onToggleSelection,
  onViewOdds,
  prediction,
  formatTime
}) => {
  const isBrazilian = getBrazilianStatus();
  const t = (text) => isBrazilian ? (footballDashboardPtBR[text] || text) : text;
  const navigate = useNavigate();
  const handleAddToChat = useAddToChat();

  // Handler to stage prediction and navigate to chat
  const handleAnalyzeInChat = async () => {
    if (!prediction || !fixture?.teams) return;

    const message = `${t('Analyze this Football prediction:')}

${fixture.teams?.away?.name} @ ${fixture.teams?.home?.name}

${t('Prediction:')} ${prediction.winner}${prediction.confidenceNumeric ? ` (${prediction.confidenceNumeric}% ${t('confidence')})` : ''}
${t('Confidence Level:')} ${prediction.confidence}
${prediction.reason ? `${t('Reasoning:')} ${prediction.reason}` : ''}

${t('Can you provide a deeper analysis of this matchup?')}`;

    await handleAddToChat(message);
    navigate('/chat');
  };

  if (!fixture) return null;

  const home = fixture?.teams?.home;
  const away = fixture?.teams?.away;

  const { teams, goals, fixture: fixtureInfo, league } = fixture;
  const isLive = ['1H', 'HT', '2H', 'ET', 'BT', 'P', 'INT', 'LIVE'].includes(fixtureInfo?.status?.short);
  const isFinished = ['FT', 'AET', 'PEN'].includes(fixtureInfo?.status?.short);
  const isUpcoming = !isLive && !isFinished;
  const status = fixtureInfo?.status?.short;
  const effectiveFrozen = isFrozen || Boolean(prediction);

  const { label, badge } = statusStyles(status);

  const formatGameTime = () => {
    if (isLive) return 'LIVE';
    if (isFinished) return 'Final';
    if (fixtureInfo?.date) return FootballService.formatTime(fixtureInfo.date);
    return 'TBD';
  };

  return (
    <div className="relative border rounded-sm p-5 bg-white/[0.03] transition-all duration-200 border-white/10">
      <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-widest text-gray-500">
        <div className="flex items-center gap-2 truncate">
          <span>{fixture?.league?.country || fixture?.league?.name || '—'}</span>
          {fixture?.league?.flag && (
            <img src={fixture.league.flag} alt={fixture.league.country} className="h-3 w-5 object-cover rounded-sm" loading="lazy" />
          )}
        </div>
        <div className="flex items-center gap-2 truncate">
          <span>{fixture?.league?.name}</span>
          {fixture?.league?.logo && (
            <img src={fixture.league.logo} alt={fixture.league.name} className="h-4 w-4 object-contain" loading="lazy" />
          )}
        </div>
      </div>

      <div className="mt-4 flex items-center justify-center text-[11px] font-mono uppercase tracking-widest text-gray-400">
        <span className={`px-2 py-0.5 rounded-full ${badge}`}>{label}</span>
      </div>

      <div className="flex items-start justify-between mt-4 gap-4">
        <div className="space-y-3 flex-1">
          {[home, away].map((team, idx) => (
            <div key={team?.id || idx} className="flex items-center gap-3">
              {team?.logo && (
                <img
                  src={team.logo}
                  alt={team.name}
                  className="w-10 h-10 object-contain border border-white/10 rounded-sm bg-black/40"
                  loading="lazy"
                />
              )}
              <p className="text-sm font-mono text-white tracking-tight leading-tight truncate">
                {team?.name || (idx === 0 ? 'Home' : 'Away')}
              </p>
            </div>
          ))}
        </div>

        <div className="flex items-stretch gap-6">
          <div className="text-right">
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500">Goals</p>
            {[goals?.home, goals?.away].map((g, idx) => (
              <p key={idx} className={`text-2xl font-black text-white tabular-nums leading-tight ${idx !== 0 ? 'mt-5' : ''}`}>
                {typeof g === 'number' ? g : '--'}
              </p>
            ))}
          </div>
        </div>
      </div>

      {(!status || ['NS'].includes(status)) && (
        <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-center gap-2 text-white/60 text-xs">
          <Clock size={12} />
          <span>{formatTime ? formatTime(fixture.fixture?.date) : fixture.fixture?.date}</span>
        </div>
      )}

      {/* Prediction Result */}
      {prediction && (
        <PredictionBox
          prediction={prediction}
          onAnalyze={handleAnalyzeInChat}
          variant="desktop"
          t={t}
        />
      )}

      <div className="mt-3 grid grid-cols-3 gap-2 items-stretch">
        {/* xPicks Button */}
        {(isUpcoming || isLive) && (
          <div className="col-span-1">
            <XPicksButton
              gameData={{
                homeTeam: teams?.home?.name,
                awayTeam: teams?.away?.name,
                homeTeamId: teams?.home?.id,
                awayTeamId: teams?.away?.id,
                leagueId: league?.id,
                date: fixtureInfo?.date,
                time: formatGameTime(),
                fixtureId: fixtureInfo?.id
              }}
              sport="football"
              label="XPICKS"
            />
          </div>
        )}

        {onToggleSelection && (
          <div className="col-span-1">
            <PredictButton
              label={
                effectiveFrozen
                  ? 'COOLDOWN'
                  : isSelected
                  ? 'SELECTED'
                  : ['1H', 'HT', '2H', 'ET', 'BT', 'P', 'INT', 'LIVE'].includes(status)
                  ? 'PREDICT LIVE'
                  : 'PREDICT'
              }
              icon={effectiveFrozen ? Clock : isSelected ? Check : Sparkles}
              isSelected={isSelected}
              isLive={['1H', 'HT', '2H', 'ET', 'BT', 'P', 'INT', 'LIVE'].includes(status)}
              isFrozen={effectiveFrozen}
              onClick={(e) => {
                e.stopPropagation();
                if (!effectiveFrozen) {
                  onToggleSelection(fixture);
                }
              }}
            />
          </div>
        )}

        {onViewOdds && (
          <div className="col-span-1">
            <ViewOddsButton
              label="VIEW ODDS"
              icon={Eye}
              onClick={() => onViewOdds(fixture)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default FootballDesktopGameCard;
